import React from 'react';
import { Location } from 'history';

import FormSection from '@reverbdotcom/commons/src/components/form_section';
import { conditionUuidForSlug } from '@reverbdotcom/commons/src/condition_helpers';

import { CSP, CanonicalProduct } from '@reverbdotcom/commons/src/gql/graphql';

import PriceGuideToolFormFields from './price_guide_tool_form_fields';
import PriceGuideToolEstimatesContainer from './price_guide_tool_estimates_container';
import PriceGuideToolFormTitle from './price_guide_tool_form_title';

interface IProps {
  csp: CSP;
  location?: Location;
}

export const CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY = 'canonicalProduct';
export const CONDITION_UUID_FORM_FIELDS_STATE_KEY = 'conditionUuid';

export interface IFormFieldsState {
  [CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY]?: CanonicalProduct;
  [CONDITION_UUID_FORM_FIELDS_STATE_KEY]?: string;
}

export enum IFormFieldsActionType {
  UPDATE = 'update',
}

export interface IFormFieldsAction {
  type: IFormFieldsActionType;
  payload: IFormFieldsState;
}

function formFieldsReducer(state: IFormFieldsState, action: IFormFieldsAction) {
  switch (action.type) {
    case IFormFieldsActionType.UPDATE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

export default function PriceGuideToolForm({ csp, location }: IProps) {
  const {
    variantId: canonicalProductIdFromQueryParams,
    conditionSlug: conditionSlugFromQueryParams,
  } = location?.query || {};

  const conditionUuidFromQueryParams = conditionUuidForSlug(conditionSlugFromQueryParams);
  const canonicalProductFromQueryParams = csp.canonicalProducts.find(({ id }) => id == canonicalProductIdFromQueryParams);

  const priceGearOnPageLoad = !!canonicalProductFromQueryParams && !!conditionUuidFromQueryParams;

  const hasMultipleCanonicalProducts = csp.canonicalProducts.length > 1;
  const canonicalProductNotFromQueryParams = hasMultipleCanonicalProducts ? {} : csp.canonicalProducts[0];
  const defaultCanonicalProduct = canonicalProductFromQueryParams || canonicalProductNotFromQueryParams;

  const initialFormFieldsState = {
    [CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY]: defaultCanonicalProduct,
    [CONDITION_UUID_FORM_FIELDS_STATE_KEY]: conditionUuidFromQueryParams || '',
  };

  const [formFieldsState, dispatch] = React.useReducer(
    formFieldsReducer,
    initialFormFieldsState,
  );

  const [showEstimate, updateShowEstimate] = React.useState(priceGearOnPageLoad);

  return (
    <div className="price-guide-tool-form">
      <FormSection
        title={
          <PriceGuideToolFormTitle
            csp={csp}
            conditionUuid={formFieldsState[CONDITION_UUID_FORM_FIELDS_STATE_KEY]}
            canonicalProduct={formFieldsState[CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY]}
            showGearCollectionButton={showEstimate}
            location={location}
          />
        }
      >
        {showEstimate ? (
          <PriceGuideToolEstimatesContainer
            csp={csp}
            canonicalProduct={formFieldsState[CANONICAL_PRODUCT_FORM_FIELDS_STATE_KEY]}
            conditionUuid={formFieldsState[CONDITION_UUID_FORM_FIELDS_STATE_KEY]}
            updateShowEstimate={updateShowEstimate}
          />
        ) : (
          <PriceGuideToolFormFields
            location={location}
            csp={csp}
            formFieldsState={formFieldsState}
            dispatch={dispatch}
            updateShowEstimate={updateShowEstimate}
          />
        )}
      </FormSection>
    </div>
  );
}
